import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { ArrowLeft, Users, Plus, Trash2, Search, CalendarPlus, CheckCircle2, AlertCircle, X, Info } from 'lucide-react';
import styles from './EquipoDashboard.module.css';
import { TeamCalendarGrid } from '@/modules/professional/components/TeamCalendarGrid';

// ─── Toast System & Lookup Maps ──────────────────────────────────────────────
export type ToastType = 'success' | 'error' | 'info';
export interface Toast { id: number; type: ToastType; message: string; }

const TOAST_BG:     Record<ToastType, string> = { success: '#F0FDF4', error: '#FEF2F2', info: '#EFF6FF' };
const TOAST_BORDER: Record<ToastType, string> = { success: '#BBF7D0', error: '#FECACA', info: '#BFDBFE' };
const TOAST_COLOR:  Record<ToastType, string> = { success: '#166534', error: '#991B1B', info: '#1E40AF' };

interface ToastsContainerProps { toasts: Toast[]; onRemove: (id: number) => void; }
function ToastsContainer({ toasts, onRemove }: Readonly<ToastsContainerProps>) {
  if (toasts.length === 0) return null;
  return (
    <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
      {toasts.map(t => (
        <div
          key={t.id}
          style={{
            pointerEvents: 'auto',
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '14px 16px', paddingRight: 40, borderRadius: 14,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            border: '1px solid',
            position: 'relative',
            minWidth: 280, maxWidth: 360,
            animation: 'slideInRight 0.25s ease',
            background:   TOAST_BG[t.type],
            borderColor:  TOAST_BORDER[t.type],
            color:        TOAST_COLOR[t.type],
          }}
        >
          {t.type === 'success' && <CheckCircle2 size={18} style={{ marginTop: 1, flexShrink: 0 }} />}
          {t.type === 'error'   && <AlertCircle  size={18} style={{ marginTop: 1, flexShrink: 0 }} />}
          {t.type === 'info'    && <Info          size={18} style={{ marginTop: 1, flexShrink: 0 }} />}
          <p style={{ margin: 0, fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>{t.message}</p>
          <button
            onClick={() => onRemove(t.id)}
            style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, opacity: 0.5 }}
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── Confirm Modal ────────────────────────────────────────────────────────────
interface ConfirmModalProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}
function ConfirmModal({ message, onConfirm, onCancel }: Readonly<ConfirmModalProps>) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        background: '#FFFFFF', borderRadius: 20, padding: '28px 32px',
        maxWidth: 380, width: '100%',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash2 size={20} color="#EF4444" />
          </div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0F172A' }}>Confirmar acción</h3>
        </div>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: '#475569', lineHeight: 1.6 }}>{message}</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{ padding: '10px 20px', borderRadius: 12, border: '1px solid #E2E8F0', background: '#F8FAFC', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            style={{ padding: '10px 20px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #EF4444, #DC2626)', color: '#FFFFFF', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Revocar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface Miembro {
  miembro_uuid: string;
  rol_equipo: string;
  perfiles_usuario: {
    nombres_completos: string | null;
    apellidos_completos: string | null;
    perfil_correo: string;
  } | null;
  estado?: string;
}

function filterToastOut(toasts: Toast[], idToRemove: number): Toast[] {
  const result: Toast[] = [];
  for (const toast of toasts) {
    if (toast.id !== idToRemove) result.push(toast);
  }
  return result;
}

// ─── Main Screen ──────────────────────────────────────────────────────────────
export function EquipoDashboardScreen() {
  const { equipo_uuid } = useParams<{ equipo_uuid: string }>();
  const navigate = useNavigate();

  const [equipoName, setEquipoName] = useState('');
  const [miembros, setMiembros] = useState<Miembro[]>([]);
  const [loading, setLoading] = useState(true);

  const [emailInput, setEmailInput] = useState('');
  const [rolInput, setRolInput] = useState('clinico_asistente');
  const [inviting, setInviting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [toasts, setToasts] = useState<Toast[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<string | null>(null);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => filterToastOut(prev, id));
  }, []);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => removeToast(id), 4000);
  }, [removeToast]);

  useEffect(() => {
    if (equipo_uuid) loadEquipoData();
  }, [equipo_uuid]);

  const loadEquipoData = async () => {
    setLoading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) setCurrentUserId(userData.user.id);

      const { data: teamData, error: teamError } = await supabase
        .from('equipos_clinicos')
        .select('nombre_display')
        .eq('equipo_uuid', equipo_uuid)
        .single();

      if (teamError) throw teamError;
      setEquipoName(teamData.nombre_display);

      const { data: membersData, error: membersError } = await supabase
        .from('equipo_miembros')
        .select(`
          miembro_uuid,
          rol_equipo,
          estado,
          perfiles_usuario!equipo_miembros_miembro_uuid_fkey (
            nombres_completos,
            apellidos_completos,
            perfil_correo
          )
        `)
        .eq('equipo_uuid', equipo_uuid);

      if (membersError) throw membersError;
      setMiembros(membersData as any);
    } catch (err) {
      console.error('Error cargando equipo:', err);
      navigate('/pro/mi-equipo');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!emailInput.trim() || !equipo_uuid) return;

    setInviting(true);
    try {
      const { data: userProfile, error: profileError } = await supabase
        .from('perfiles_usuario')
        .select('perfil_usuario_id')
        .ilike('perfil_correo', emailInput.trim())
        .single();

      if (profileError || !userProfile) {
        addToast('error', 'No se encontró un usuario de Aura con ese correo.');
        return;
      }

      const { data, error } = await supabase.rpc('fn_invitar_miembro_equipo', {
        p_equipo_uuid: equipo_uuid,
        p_miembro_uuid: userProfile.perfil_usuario_id,
        p_rol: rolInput
      });

      if (error) throw error;

      if (data?.success === false) {
        addToast('error', data.error || 'No se pudo enviar la invitación.');
      } else {
        addToast('success', 'Invitación enviada. El usuario recibirá una notificación para aceptar o rechazar.');
        setEmailInput('');
        loadEquipoData();
      }
    } catch (err: any) {
      console.error('Error invitando miembro:', err);
      addToast('error', 'Error al enviar la invitación: ' + err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = (miembro_uuid: string) => {
    setConfirmTarget(miembro_uuid);
  };

  const confirmRemove = async () => {
    if (!confirmTarget) return;
    setConfirmTarget(null);
    setRemovingId(confirmTarget);
    try {
      const { error } = await supabase
        .from('equipo_miembros')
        .delete()
        .eq('equipo_uuid', equipo_uuid)
        .eq('miembro_uuid', confirmTarget);

      if (error) throw error;

      setMiembros(prev => prev.filter(m => m.miembro_uuid !== confirmTarget));
      addToast('success', 'Miembro revocado correctamente.');
    } catch (err: any) {
      console.error('Error removing member:', err);
      addToast('error', 'Ocurrió un error al revocar al miembro.');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) return <div className="p-8 text-center">Cargando tablero del equipo...</div>;

  const isCreator = miembros.find(m => m.miembro_uuid === currentUserId)?.rol_equipo === 'clinico_principal';

  return (
    <div className={styles.container}>
      <ToastsContainer toasts={toasts} onRemove={removeToast} />
      {confirmTarget && (
        <ConfirmModal
          message="¿Estás seguro de que deseas revocar el acceso a este miembro del equipo? Esta acción se puede deshacer volviendo a invitarlo."
          onConfirm={confirmRemove}
          onCancel={() => setConfirmTarget(null)}
        />
      )}

      <div className={styles.pageHeader}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} color="#1E293B" />
        </button>
        <h1 className={styles.pageTitle}>
          <Users size={32} color="#312E81" />
          {equipoName}
        </h1>
        <p className={styles.pageSubtitle}>
          Dashboard Centralizado - Gestión Interdisciplinaria
        </p>
      </div>

      <div className={styles.dashboardContent}>
        <div className={styles.card}>
          <div className={styles.cardHeaderFlex}>
            <h2 className={styles.cardTitle} style={{ margin: 0 }}>Agenda del Equipo</h2>
            <div className={styles.calendarActions}>
              <div className={styles.searchWrapper}>
                <Search size={18} color="#94A3B8" className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Buscar paciente o médico..."
                  className={styles.searchInput}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                className={styles.newAptBtn}
                onClick={() => addToast('info', 'La agenda de Citas Grupales se habilitará en la próxima actualización.')}
                title="Próximamente"
              >
                <CalendarPlus size={18} style={{ marginRight: 6 }} />
                Nueva Cita Grupal
              </button>
            </div>
          </div>

          {/* Solo mostramos miembros activos en la grilla */}
          <TeamCalendarGrid
            equipo_uuid={equipo_uuid!}
            miembros={miembros.filter(m => m.estado !== 'pendiente')}
            currentUserId={currentUserId!}
          />
        </div>

        {isCreator && (
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Administrar Miembros</h2>

            <form onSubmit={handleAddMember} className={styles.formWrapper}>
              <input
                type="email"
                placeholder="Correo del usuario a invitar (registrado en Aura)"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className={styles.input}
                required
              />
              <select
                value={rolInput}
                onChange={(e) => setRolInput(e.target.value)}
                className={styles.select}
              >
                <option value="clinico_asistente">Clínico Asociado</option>
                <option value="secretaria">Secretaria de Equipo</option>
              </select>
              <button type="submit" className={styles.button} disabled={inviting || !emailInput}>
                {inviting ? <div className={styles.spinnerWhite} /> : <><Plus size={18} style={{ marginRight: 6 }} /> Añadir</>}
              </button>
            </form>

            <div className={styles.memberList}>
              {miembros.map(m => (
                <div key={m.miembro_uuid} className={styles.memberItem}>
                  <div className={styles.memberInfo}>
                    <p className={styles.memberName}>
                      {m.perfiles_usuario?.nombres_completos} {m.perfiles_usuario?.apellidos_completos}
                      {m.miembro_uuid === currentUserId && ' (Tú)'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <p className={styles.memberRole}>
                        {m.rol_equipo === 'clinico_asistente' ? 'Clínico Asociado' : m.rol_equipo.replace('_', ' ')}
                      </p>
                      {m.estado === 'pendiente' && (
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#92400E', backgroundColor: '#FEF3C7', padding: '2px 6px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Pendiente
                        </span>
                      )}
                    </div>
                  </div>
                  {m.rol_equipo !== 'clinico_principal' && (
                    <button
                      className={styles.removeBtn}
                      onClick={() => handleRemoveMember(m.miembro_uuid)}
                      disabled={removingId === m.miembro_uuid}
                      title="Revocar miembro"
                    >
                      {removingId === m.miembro_uuid ? (
                        <div className={styles.spinnerWhite} style={{ borderColor: 'rgba(239,68,68,0.3)', borderTopColor: '#EF4444', width: 16, height: 16 }} />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
